"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useHotkeys } from "react-hotkeys-hook";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { coverForId } from "@/lib/image/covers";
import {
  getGetProjectQueryKey,
  getListProjectsQueryKey,
  useGetProject,
  useSetProjectCover,
  useUpdateProject,
} from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetClose,
  SheetBody,
  SheetFooter,
} from "@/components/ui/sheet";
import { SubmitButton } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import { UsersSelect } from "@/components/ui/users-select";
import { useAuth } from "@/features/auth";
import { Icon } from "@/components/ui/icon";
import { CoverUpload } from "../cover-upload";
import { Pending } from "./pending";
import {
  manageProjectFormSchema,
  type ManageProjectFormValues,
} from "@/features/projects/lib/project-form-schema";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

interface ManageProjectProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  projectId: string | null;
}

export function ManageProject({
  open,
  onClose,
  onSaved,
  projectId,
}: ManageProjectProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const formId = useId();
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string>("");
  const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const { data: project, isLoading: projectLoading } = useGetProject(
    projectId ?? "",
    { query: { enabled: Boolean(projectId) && open } },
  );

  const form = useForm<ManageProjectFormValues>({
    resolver: zodResolver(manageProjectFormSchema),
    mode: "onTouched",
    defaultValues: {
      title: "",
      description: "",
      accessLevel: "shared",
      collaboratorIds: [],
    },
  });
  const { control, handleSubmit, reset: resetForm } = form;
  const accessLevel = useWatch({ control, name: "accessLevel" });

  const handlePickCoverFile = useCallback((file: File) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setPendingCoverFile(file);
    setCoverPreviewUrl(url);
  }, []);

  useEffect(() => {
    if (!project || !open) return;
    resetForm({
      title: project.title,
      description: project.description || "",
      accessLevel: project.access_level as "shared" | "private",
      collaboratorIds: (project.collaborators ?? []).map((c) => c.user_id),
    });
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync server-fetched project + open transition into local cover state
    setPendingCoverFile(null);
    setCoverPreviewUrl(project.cover_image_url || coverForId(project.id));
  }, [project, open, resetForm]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  const collaboratorExcludeIds = [user?.id, project?.created_by].filter(
    (id): id is string => Boolean(id),
  );

  const updateProjectMutation = useUpdateProject({
    mutation: {
      onSuccess: () => {
        if (projectId) {
          queryClient.invalidateQueries({
            queryKey: getListProjectsQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetProjectQueryKey(projectId),
          });
        }
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
        onSaved();
        onClose();
        showToast.success("Project updated");
      },
      onError: (err) => {
        if (isPermissionDenied(err)) {
          queryClient.invalidateQueries({ queryKey: ["projects"] });
          if (projectId) {
            queryClient.invalidateQueries({
              queryKey: getListProjectsQueryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: getGetProjectQueryKey(projectId),
            });
          }
          showToast.error("Permission denied", {
            description: getPermissionErrorMessage(err),
          });
          onClose();
        } else {
          const message = err instanceof Error ? err.message : "Save failed";
          showToast.error("Save failed", { description: message });
        }
      },
    },
  });

  const setCoverMutation = useSetProjectCover({
    mutation: {
      onError: (err) => {
        if (isPermissionDenied(err)) {
          showToast.error("Permission denied", {
            description: getPermissionErrorMessage(err),
          });
        } else {
          const message =
            err instanceof Error ? err.message : "Cover upload failed";
          showToast.error("Cover upload failed", { description: message });
        }
      },
    },
  });

  const onValid = useCallback(
    async (values: ManageProjectFormValues) => {
      if (updateProjectMutation.isPending || !projectId) return;

      // Cover first — its own request — then a single PATCH that carries the
      // new title/description/access_level *and* the collaborator set, so the
      // backend reconciles access-level + collaborators in one transaction
      // (otherwise switching shared→private + adding collaborators in the
      // same UI save races and the collaborator add 400s on the still-shared
      // project).
      if (pendingCoverFile) {
        try {
          await setCoverMutation.mutateAsync({
            projectId,
            data: { file: pendingCoverFile },
          });
          setPendingCoverFile(null);
        } catch {
          return;
        }
      }

      updateProjectMutation.mutate({
        projectId,
        data: {
          title: values.title,
          description: values.description.trim() || undefined,
          access_level: values.accessLevel,
          collaborator_ids:
            values.accessLevel === "private" ? values.collaboratorIds : null,
        },
      });
    },
    [pendingCoverFile, projectId, setCoverMutation, updateProjectMutation],
  );

  const runSave = useCallback(() => {
    void handleSubmit(onValid)();
  }, [handleSubmit, onValid]);

  useHotkeys(
    "mod+enter",
    (e) => {
      e.preventDefault();
      runSave();
    },
    { enabled: open, preventDefault: true, enableOnFormTags: true },
    [open, runSave],
  );

  return (
    <Sheet
      open={open && !!projectId}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetHeader>
        <div className="flex items-center gap-1.5">
          <Icon name="IconFolderBookmarks" className="size-4" />
          <SheetTitle>Manage Project</SheetTitle>
        </div>
        <SheetClose />
      </SheetHeader>

      <SheetBody>
        <Form {...form}>
          {projectLoading || !project ? (
            <Pending />
          ) : (
            <form
              id={formId}
              onSubmit={handleSubmit(onValid)}
              className="flex flex-col gap-5"
            >
              <CoverUpload
                previewUrl={coverPreviewUrl}
                onPickFile={handlePickCoverFile}
                uploading={setCoverMutation.isPending}
                variant="sheet"
              />

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="flex items-center gap-1.5 border-b border-border bg-surface-header px-4 py-3">
                  <Icon
                    name="IconNewspaper1"
                    size={14}
                    className="text-heading opacity-50"
                  />
                  <span className="text-sm font-medium text-heading">
                    Details
                  </span>
                </div>
                <div className="flex flex-col gap-4 px-4 py-4">
                  <FormField
                    control={control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel required>Project Title</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            variant="project"
                            size="default"
                            placeholder="e.g. Engineering"
                            maxLength={100}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="accessLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel id="manage-access-level-label">
                          Access Level
                        </FormLabel>
                        <div
                          role="radiogroup"
                          aria-labelledby="manage-access-level-label"
                          aria-describedby="manage-access-level-help"
                          className="flex items-center gap-2"
                        >
                          <button
                            type="button"
                            role="radio"
                            aria-checked={field.value === "shared"}
                            onClick={() => field.onChange("shared")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                              field.value === "shared"
                                ? "bg-primary/15 border-primary/30 text-primary dark:text-[#7ba3ff]"
                                : "bg-contrast/3 border-border text-contrast/50 hover:bg-contrast/6"
                            }`}
                          >
                            Shared
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={field.value === "private"}
                            onClick={() => field.onChange("private")}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                              field.value === "private"
                                ? "bg-primary/15 border-primary/30 text-primary dark:text-[#7ba3ff]"
                                : "bg-contrast/3 border-border text-contrast/50 hover:bg-contrast/6"
                            }`}
                          >
                            Private
                          </button>
                        </div>
                        <p
                          id="manage-access-level-help"
                          className="text-xs text-contrast/40 leading-4"
                        >
                          {field.value === "shared"
                            ? "Visible to everyone in your organization."
                            : "Only visible to you, collaborators, and admins."}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel optional>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            variant="project"
                            placeholder="Describe what this project is for"
                            rows={4}
                            maxLength={255}
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <AnimatePresence>
                    {accessLevel === "private" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <FormField
                          control={control}
                          name="collaboratorIds"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Collaborators</FormLabel>
                              <FormControl>
                                <UsersSelect
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select collaborators..."
                                  disabled={updateProjectMutation.isPending}
                                  excludeIds={collaboratorExcludeIds}
                                />
                              </FormControl>
                              <FormDescription>
                                Search active organization members and add the
                                teammates who should have access to this private
                                project.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </form>
          )}
        </Form>
      </SheetBody>

      <SheetFooter>
        <SubmitButton
          type="submit"
          form={formId}
          disabled={
            updateProjectMutation.isPending ||
            setCoverMutation.isPending ||
            projectLoading ||
            !project
          }
          loading={
            updateProjectMutation.isPending || setCoverMutation.isPending
          }
          loadingLabel="Save Changes"
          variant="primary"
          size="sm"
        >
          Save Changes <KbdShortcut keys={["mod", "↵"]} variant="primary" />
        </SubmitButton>
      </SheetFooter>
    </Sheet>
  );
}
